import React, { useState } from 'react';
import { RadioSymbol } from 'ui';
import { ComplexValue, LoadOptions } from 'app/types-shared';
import { SelectSingle } from 'app/area/shared/translated-components';

import { getFilterId } from '../utils/get-filter-id';
import { FilterState } from '../types-store';
import { FilterCommonProps } from './filter-common';
import { FilterValidationWrapper } from './filter-validation-wrapper';

// eslint:disable-next-line: max-func-body-length
export const FilterDate: React.FC<FilterCommonProps> = ({
  filter, updateFilter,
  endpoints, options, loadOptions,
  validation, translation
}) => {

  const noDefaultDateId = getFilterId(filter.domain?.value, 'noDefaultDate');
  const predefinedId = getFilterId(filter.domain?.value, 'predefined');
  const rangeId = getFilterId(filter.domain?.value, 'range');
  const rangeFromId = getFilterId(filter.domain?.value, 'rangeFrom');
  const rangeToId = getFilterId(filter.domain?.value, 'rangeTo');

  const onChangePredefined = (value: ComplexValue<number>) => {

    const { periodPredef: periodPredefValidation, ...filteredValidation } = (validation ?? {});

    const updatedValidation = periodPredefValidation && value.value !== filter.periodPredef?.value
      ? filteredValidation
      : validation;

    updateFilter(
      filter.domain?.value,
      { ...filter, periodPredef: value },
      updatedValidation
    );
  };

  const [predefinedLoadOptions, setPredefinedLoadOptions] = useState<LoadOptions>();

  const onSetPredefinedLoadOptions = (loadOptionsParams: LoadOptions) => {
    loadOptions(predefinedId, endpoints.periodPredefOptions, loadOptionsParams);
    setPredefinedLoadOptions(loadOptionsParams);
  };

  const onChangeRangeFrom = (value: ComplexValue<number>) => {

    const { defaultValue: defaultValueValidation, ...filteredValidation } = (validation ?? {});

    const updatedValidation = defaultValueValidation && value.value !== filter.defaultValue?.value
      ? filteredValidation
      : validation;

    updateFilter(
      filter.domain?.value,
      { ...filter, defaultValue: value },
      updatedValidation
    );
  };

  const [rangeFromLoadOptions, setRangeFromLoadOptions] = useState<LoadOptions>();

  const onSetRangeFromLoadOptions = (loadOptionsParams: LoadOptions) => {
    loadOptions(rangeFromId, endpoints.dbFunction, loadOptionsParams);
    setRangeFromLoadOptions(loadOptionsParams);
  };

  const onChangeRangeTo = (value: ComplexValue<number>) => {

    const {
      defaultValue: defaultValueValidation,
      defaultValue2: defaultValue2Validation,
      ...filteredValidation
    } = (validation ?? {});

    const updatedValidation = (defaultValueValidation || defaultValue2Validation)
      && value.value !== filter.defaultValue2?.value
      ? filteredValidation
      : validation;

    updateFilter(
      filter.domain?.value,
      { ...filter, defaultValue2: value },
      updatedValidation
    );
  };

  const [rangeToLoadOptions, setRangeToLoadOptions] = useState<LoadOptions>();

  const onSetRangeToLoadOptions = (loadOptionsParams: LoadOptions) => {
    loadOptions(rangeToId, endpoints.dbFunction, loadOptionsParams);
    setRangeToLoadOptions(loadOptionsParams);
  };

  const changeFilterToNoDefaultDate = () => {

    updateFilter(
      filter.domain?.value,
      {
        ...filter,
        defaultValue: undefined,
        defaultValue2: undefined,
        myValue: false,
        periodPredef: undefined,
        state: FilterState.NoDefaultValue
      },
      {}
    );
  };

  const changeFilterToFixed = () => {

    const { defaultValue: _, defaultValue2: __, ...filteredValidation } = (validation ?? {});

    updateFilter(
      filter.domain?.value,
      {
        ...filter,
        defaultValue: undefined,
        defaultValue2: undefined,
        state: FilterState.FixedPeriod
      },
      filteredValidation
    );
  };

  const changeFilterToRange = () => {

    const { periodPredef: _, ...filteredValidation } = (validation ?? {});

    updateFilter(
      filter.domain?.value,
      { ...filter, periodPredef: undefined, state: FilterState.Range },
      filteredValidation
    );
  };

  return <>
    <div className="form-check">
      <input
        id={`${noDefaultDateId}-select`}
        type="radio"
        className="form-check-input visuallyhidden"
        value={FilterState.NoDefaultValue}
        checked={filter.state === FilterState.NoDefaultValue}
        onChange={changeFilterToNoDefaultDate} />
      <label
        htmlFor={`${noDefaultDateId}-select`}
        className="form-check-label">
        <RadioSymbol />
        <b>{translation.no_default_value}</b>
      </label>
    </div>
    <div className="form-check">
      <input
        id={`${predefinedId}-select`}
        type="radio"
        className="form-check-input visuallyhidden"
        value={FilterState.FixedPeriod}
        checked={filter.state === FilterState.FixedPeriod}
        onChange={changeFilterToFixed} />
      <label
        htmlFor={`${predefinedId}-select`}
        className="form-check-label">
        <RadioSymbol />
        <b>{translation.filters_date_range_date_fixed_period}</b>
      </label>
      {filter.state === FilterState.FixedPeriod &&
        <SelectSingle
          componentId={predefinedId}
          className={undefined}
          valueItem={filter.periodPredef?.value ? filter.periodPredef : undefined}
          onChange={onChangePredefined}
          validation={validation?.periodPredef}

          options={options && options[predefinedId]?.codes as ComplexValue<number>[] || []}
          fetching={options && options[predefinedId]?.fetching || false}
          loadOptions={predefinedLoadOptions}
          onLoadOptions={onSetPredefinedLoadOptions}

          placeholder={translation?.search} />}
    </div>
    <div className="form-check">
      <input
        id={`${rangeId}-select`}
        type="radio"
        className="form-check-input visuallyhidden"
        value={FilterState.Range}
        checked={filter.state === FilterState.Range}
        onChange={changeFilterToRange} />
      <label
        htmlFor={`${rangeId}-select`}
        className="form-check-label">
        <RadioSymbol />
        <b>{translation.filters_date_range_date_interval}</b>
      </label>

      {filter.state === FilterState.Range && <FilterValidationWrapper validationProp={validation?.defaultValue}>
        <SelectSingle
          componentId={rangeFromId}
          className={undefined}
          label={{ text: translation.filters_date_range_from, visible: true }}
          valueItem={filter.defaultValue?.value ? filter.defaultValue : undefined}
          onChange={onChangeRangeFrom}

          options={options && options[rangeFromId]?.codes as ComplexValue<number>[] || []}
          fetching={options && options[rangeFromId]?.fetching || false}
          loadOptions={rangeFromLoadOptions}
          onLoadOptions={onSetRangeFromLoadOptions}

          placeholder={translation?.search} />
        <SelectSingle
          componentId={rangeToId}
          className={undefined}
          label={{ text: translation.filters_date_range_to, visible: true }}
          valueItem={filter.defaultValue2?.value ? filter.defaultValue2 : undefined}
          onChange={onChangeRangeTo}

          options={options && options[rangeToId]?.codes as ComplexValue<number>[] || []}
          fetching={options && options[rangeToId]?.fetching || false}
          loadOptions={rangeToLoadOptions}
          onLoadOptions={onSetRangeToLoadOptions}

          placeholder={translation?.search} />
      </FilterValidationWrapper>}
    </div>
  </>;
};
